// Profil (design_handoff_profil §2): Hero-Widget mit Avatar-Auswahl, Meldungsbalken und Segment-Tabs.
// Die Handler kommen unverändert aus ProfileView.
import type { ChangeEvent, RefObject } from 'react';
import { CalendarBlank, Camera, CheckCircle, CircleNotch, EnvelopeSimple, Shield, ShieldStar, User, WarningCircle, X } from '@phosphor-icons/react';
import { getInitials, rollenLabel, statusLabel, type ProfilDaten } from '../../../../lib/profil/profil';

export type ProfilTab = 'general' | 'security';

export function Hero({ profil, schmal, uploading, fileInputRef, onAvatarClick, onFileSelect }: {
  profil: ProfilDaten | null; schmal: boolean; uploading: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>; onAvatarClick: () => void; onFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  const groesse = schmal ? 108 : 128;
  const ausrichtung = schmal ? 'center' : 'flex-start';
  return (
    <section aria-label="Profil" className="immo-glas flex items-center"
      style={{ borderRadius: 32, padding: schmal ? '24px 20px' : '28px 32px', gap: schmal ? 18 : 28, flexDirection: schmal ? 'column' : 'row', textAlign: schmal ? 'center' : 'left' }}>
      <input type="file" ref={fileInputRef} onChange={onFileSelect} className="hidden" accept="image/*" />
      <button type="button" onClick={onAvatarClick} aria-label="Profilbild ändern" data-tip="Profilbild ändern – Bild auswählen, zuschneiden und speichern"
        className="relative shrink-0 transition-transform duration-300 hover:scale-[1.04]" style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '50%' }}>
        <span className="relative flex items-center justify-center overflow-hidden"
          style={{ width: groesse, height: groesse, borderRadius: '50%', background: 'var(--acc-fill)', color: 'var(--acc-ink)',
            boxShadow: '0 0 0 4px var(--sheet), 0 18px 40px -16px var(--shade)' }}>
          {profil?.avatar_url
            ? <img src={profil.avatar_url} alt="Profilbild" className="h-full w-full object-cover" />
            : <span style={{ fontSize: schmal ? 34 : 40, fontWeight: 800, letterSpacing: '-0.02em' }}>{getInitials(profil?.full_name)}</span>}
          {uploading && (
            <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--sheet) 60%, transparent)', backdropFilter: 'blur(6px)', color: 'var(--acc)' }}>
              <CircleNotch size={26} weight="bold" className="animate-spin" />
            </span>
          )}
        </span>
        <span className="absolute flex items-center justify-center"
          style={{ right: 2, bottom: 2, width: 36, height: 36, borderRadius: '50%', background: 'var(--sheet)', color: 'var(--ink)', boxShadow: '0 6px 16px -6px var(--shade), inset 0 0 0 1px var(--line)' }}>
          <Camera size={17} weight="fill" />
        </span>
      </button>
      <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 8, alignItems: ausrichtung }}>
        <div className="flex flex-wrap" style={{ gap: 6, justifyContent: ausrichtung }}>
          <span data-tip="Rolle – Bestimmt die Berechtigungen im Portal" className="inline-flex items-center"
            style={{ height: 24, padding: '0 10px', gap: 5, borderRadius: 12, fontSize: 12, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
            <ShieldStar size={13} weight="fill" />{rollenLabel(profil?.role)}
          </span>
          <span data-tip="Status – Konto-Status laut Profil" className="inline-flex items-center"
            style={{ height: 24, padding: '0 10px', gap: 6, borderRadius: 12, fontSize: 12, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)' }} />{statusLabel(profil?.status)}
          </span>
        </div>
        <h1 className="m-0" style={{ fontSize: schmal ? 28 : 38, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05, overflowWrap: 'anywhere' }}>{profil?.full_name}</h1>
        <div className="flex flex-wrap" style={{ gap: '6px 16px', justifyContent: ausrichtung, fontSize: 14, color: 'var(--ink2)' }}>
          <span className="inline-flex min-w-0 items-center" style={{ gap: 6 }}><EnvelopeSimple size={15} color="var(--ink3)" className="shrink-0" /><span className="truncate">{profil?.email}</span></span>
          <span className="inline-flex items-center" style={{ gap: 6 }}>
            <CalendarBlank size={15} color="var(--ink3)" />Seit {new Date(profil?.created_at ?? '').toLocaleDateString('de-DE')}
          </span>
        </div>
      </div>
    </section>
  );
}

/** Meldungsbalken für error (rot) bzw. success (grün) – gleiche States wie bisher. */
export function Meldung({ art, text, onSchliessen, mobil = false }: { art: 'fehler' | 'erfolg'; text: string; onSchliessen: () => void; mobil?: boolean }) {
  const fehler = art === 'fehler';
  const I = fehler ? WarningCircle : CheckCircle;
  return (
    <div role={fehler ? 'alert' : 'status'} className="flex items-center"
      style={{ gap: 12, padding: '12px 16px', borderRadius: 20, fontSize: 13.5, fontWeight: 700,
        background: fehler ? 'var(--bad-soft)' : 'var(--ok-soft)', color: fehler ? 'var(--bad)' : 'var(--ok)' }}>
      <I size={20} weight="fill" className="shrink-0" />
      <span className="min-w-0 flex-1" style={{ overflowWrap: 'anywhere' }}>{text}</span>
      <button type="button" onClick={onSchliessen} aria-label="Meldung ausblenden" data-tip="Schließen – Meldung ausblenden"
        className="flex shrink-0 items-center justify-center" style={{ width: mobil ? 44 : 30, height: mobil ? 44 : 30, border: 'none', borderRadius: '50%', cursor: 'pointer', background: 'transparent', color: 'inherit' }}>
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}

const TABS: { key: ProfilTab; label: string; icon: typeof User; tip: string }[] = [
  { key: 'general', label: 'Allgemein', icon: User, tip: 'Allgemein – Persönliche Daten, Account Status und Telefonie' },
  { key: 'security', label: 'Sicherheit', icon: Shield, tip: 'Sicherheit – Passwort, Login-Schutz und Login-Historie' },
];

/** Segment-Tabs mittig (aktiv = Akzent-Füllung), Pfeiltasten wechseln. */
export function ProfilTabs({ wert, onChange, mobil }: { wert: ProfilTab; onChange: (t: ProfilTab) => void; mobil: boolean }) {
  return (
    <div role="tablist" aria-label="Profil-Bereiche" className="immo-glas flex self-center"
      onKeyDown={(e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const leiste = e.currentTarget;
        onChange(wert === 'general' ? 'security' : 'general');
        requestAnimationFrame(() => leiste.querySelector<HTMLElement>('[aria-selected="true"]')?.focus());
      }}
      style={{ gap: 4, padding: 4, borderRadius: 20 }}>
      {TABS.map((t) => {
        const an = t.key === wert;
        const I = t.icon;
        return (
          <button key={t.key} type="button" role="tab" aria-selected={an} tabIndex={an ? 0 : -1} data-tip={t.tip} onClick={() => onChange(t.key)}
            className="inline-flex items-center whitespace-nowrap"
            style={{ height: mobil ? 44 : 40, padding: '0 18px', gap: 8, borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
              background: an ? 'var(--acc-fill)' : 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)', boxShadow: an ? 'var(--acc-sh)' : 'none', transition: 'background .2s, color .2s' }}>
            <I size={17} weight={an ? 'fill' : 'bold'} />{t.label}
          </button>
        );
      })}
    </div>
  );
}
